import { AxiosRequestConfig } from 'axios';
import moment from 'moment';
import { ITransactionLookupDto } from 'src/common/interfaces/dtos/ITransactionLookup';
import {
  ITransactionLookupData,
  ITransactionLookupResponse,
} from 'src/common/interfaces/dtos/ITransactionLookupData';
import { CartStatus } from 'src/items-list/business-logic/service/enumerations/cart-status';
import {
  CartCustomerData,
  CartResponse,
} from 'src/items-list/business-logic/service/interfaces/cart-response';
import { ActivityType } from 'src/operation-notify/enum/activities.enum';
import { CustomerDetailsInfo } from 'src/return-services-display/interfaces';
import { emeraldRequest } from '../api/emerald-request';
import { ICustomer, ICustomerRequestParameters } from '../interfaces/dtos/ICustomer';
import { EmeraldRequestParams } from '../interfaces/requests/EmeraldRequestParams';
import { store } from '../redux/configure-store';
import { extractLoyaltyID } from '../../customer-loyalty/business-logic/set-customer';
import { sortSavedTransactions } from 'src/search-transactions/components/saved-transaction/saved-transactions-screen';
import { IPagedData } from '../interfaces/dtos/IPagedData';

export interface ITransactionListing {
  suspendTime: string;
  touchPointId: string;
  transactionId: string;
  sequenceNumber: number;
  totalOrderAmount: number;
  cartStatus?: string;
  cartReference?: CartReference;
}

export interface ITransactionScanAndGoListing {
  sequenceNumber: number;
  transactionId: string;
  touchPointId: string;
  workstationId: string;
  beginDateTime: string;
  totalAmount: number;
  customerOrderId: string;
}

export interface ISavedTransaction {
  messageId: number;
  tillId?: string;
  transactionId: string;
  isBarCode: boolean;
  cartReference?: CartReference;
  isTrainingMode?: boolean;
  cartStatus?: CartStatus;
}

export interface CartReference {
  type: string;
  value: string;
}
export interface IMergeTransactionParams {
  currentTillId: string;
  currentTransactionId: string;
  tillId: string;
  transactionNumber: string;
  isBarCode: boolean;
}

export interface ISetTransactionCustomerParams {
  transactionId: string;
  customer: ICustomer;
  entryMethod: string;
  searchAndAdd: boolean;
}

export interface IRemoveCustomerFromTransactionParams {
  transactionId: string;
  consumerAccountNumber: string;
}

interface CustomerCredentials {
  firstName?: string;
  lastName?: string;
  address?: string;
  city?: string;
  province?: string;
  postalCode?: string;
}

export interface GetRewardApprovals {
  redeemLoyalty: RewardApprovalBody;
}
export interface RewardApprovalBody {
  approved: boolean;
  promotionId: string;
  programId: string;
  amount?: number;
}

const buildCustomerCredentials = (customerInfo: CustomerDetailsInfo): CustomerCredentials => {
  const credentials = {};
  Object.keys(customerInfo).forEach(key => {
    if (customerInfo[key].required) {
      credentials[key] = customerInfo[key].value;
    }
  });

  return credentials;
};

export const salesTransactionBegin = async (
  storeId: string,
  tillId: string,
  trainingMode: boolean,
  typeCode?: ActivityType,
  customerDetails?: CustomerDetailsInfo,
  tLogId?: string,
  tenderExchangeId?: string,
): Promise<CartResponse> => {
  const dateTime = store.getState().user.trainingModeEffectiveDatetime;
  let beginDateTime = moment(new Date())
    .utc()
    .toISOString();
  const businessDate = moment(new Date())
    .utc()
    .toISOString();

  if (trainingMode) {
    if (dateTime) {
      beginDateTime = dateTime;
    }
  }

  const axiosConfig: AxiosRequestConfig = {
    method: 'POST',
    url: '/transaction-management/transactions',
    data: {
      storeId,
      tillId,
      businessDate,
      beginDateTime,
      trainingMode,
      typeCode,
      tLogId,
      tenderExchangeId,
    },
  };

  if (customerDetails) {
    axiosConfig.data = {
      ...axiosConfig.data,
      customerCredentials: buildCustomerCredentials(customerDetails),
    };
  }

  const emeraldRequestParams: EmeraldRequestParams = {
    config: axiosConfig,
    addPOSMetaData: true,
    showLoggingInfo: true,
  };

  return emeraldRequest<CartResponse>(emeraldRequestParams);
};
export interface transactionsListings {
  touchpointId?: string;
  scanData?: string;
  pageNumber?: number;
  pageSize?: number;
  status?: CartStatus;
  isTrainingMode?: boolean;
}
export const getTransactionListings = async (
  params?: transactionsListings,
): Promise<ITransactionListing[]> => {
  const isTrainingMode = store.getState().posMode.trainingMode;
  params = {
    ...params,
    isTrainingMode: isTrainingMode ?? false,
  };

  const axiosConfig: AxiosRequestConfig = {
    method: 'GET',
    url: '/transaction-management/transactions',
    params,
  };

  const emeraldRequestParams: EmeraldRequestParams = {
    config: axiosConfig,
    addPOSMetaData: true,
    showLoadingComponent: false,
    showLoggingInfo: true,
  };

  const response = await emeraldRequest<ITransactionListing[]>(emeraldRequestParams);
  return sortSavedTransactions(response);
};

export const getTransactionsScanAndGoLookup = async (
  tillId?: string,
): Promise<ITransactionLookupData[]> => {
  const config: ITransactionLookupDto = {
    status: CartStatus.Suspended,
    isTripEnded: true,
    tillId,
  };

  const axiosConfig: AxiosRequestConfig = {
    url: `/transaction-management/transactions/lookup`,
    data: config,
    method: 'POST',
  };

  const emeraldRequestParams: EmeraldRequestParams = {
    config: axiosConfig,
    addPOSMetaData: true,
    showLoadingComponent: false,
    showLoggingInfo: true,
  };

  const response = await emeraldRequest<ITransactionLookupResponse>(emeraldRequestParams);
  return response.transactionLookups;
};

export const resumeSavedTransaction = async (
  savedParams?: ISavedTransaction,
): Promise<CartResponse> => {
  const isTrainingMode = store.getState().posMode.trainingMode;
  const params = {
    isTrainingMode,
    ...savedParams,
  };

  const axiosConfig: AxiosRequestConfig = {
    method: 'POST',
    url: '/transaction-management/resume-transaction',
    data: params,
  };

  const emeraldRequestParams: EmeraldRequestParams = {
    config: axiosConfig,
    addPOSMetaData: true,
    showLoadingComponent: false,
    showLoggingInfo: true,
  };
  return emeraldRequest<CartResponse>(emeraldRequestParams);
};

export const updateRecallCartStatus = async (
  savedParams?: ISavedTransaction,
): Promise<CartResponse> => {
  const isTrainingMode = store.getState().posMode.trainingMode;
  const params = {
    isTrainingMode,
    ...savedParams,
  };

  const axiosConfig: AxiosRequestConfig = {
    method: 'POST',
    url: '/transaction-management/update-cart-status',
    data: params,
  };

  const emeraldRequestParams: EmeraldRequestParams = {
    config: axiosConfig,
    addPOSMetaData: true,
    showLoadingComponent: false,
    showLoggingInfo: true,
  };
  return emeraldRequest<CartResponse>(emeraldRequestParams);
};

export const mergeTransaction = async (params: IMergeTransactionParams): Promise<CartResponse> => {
  // R10: currently only used and works for r10 configurations
  const axiosConfig: AxiosRequestConfig = {
    url: '/transaction-management/merge',
    data: params,
    method: 'POST',
  };

  const emeraldRequestParams: EmeraldRequestParams = {
    config: axiosConfig,
    addPOSMetaData: true,
    showLoadingComponent: false,
    showLoggingInfo: true,
  };

  return emeraldRequest<CartResponse>(emeraldRequestParams);
};

export const addCustomer = (params: ISetTransactionCustomerParams): Promise<CartResponse> => {
  const customer: ICustomerRequestParameters = {
    consumerAccountNumber: params.customer.consumerAccountNumber || '',
    loyaltyId: extractLoyaltyID(params.customer),
    entryMethod: params.entryMethod,
    searchAndAdd: params.searchAndAdd,
    customer: params.customer,
    touchpointId: store.getState().touchPoint.touchPointConfiguration.touchPointId,
  };
  const axiosConfig: AxiosRequestConfig = {
    url: `/transaction-management/transactions/${params.transactionId}/customer`,
    method: 'POST',
    data: customer,
  };

  const emeraldRequestParams: EmeraldRequestParams = {
    config: axiosConfig,
    addPOSMetaData: true,
    showLoggingInfo: true,
  };

  return emeraldRequest<CartResponse>(emeraldRequestParams);
};

export const removeCustomerFromTransaction = (
  params: IRemoveCustomerFromTransactionParams,
): Promise<CartResponse> => {
  const axiosConfig: AxiosRequestConfig = {
    url: `/transaction-management/transactions/${params.transactionId}/customer/${params.consumerAccountNumber}`,
    method: 'DELETE',
  };

  const emeraldRequestParams: EmeraldRequestParams = {
    config: axiosConfig,
    addPOSMetaData: true,
    showLoadingComponent: false,
    showLoggingInfo: true,
  };

  return emeraldRequest<CartResponse>(emeraldRequestParams);
};

export const getCustomer = (transactionId: string): Promise<CartCustomerData> => {
  const axiosConfig: AxiosRequestConfig = {
    url: `/transaction-management/transactions/${transactionId}/customer`,
    method: 'GET',
  };

  const emeraldRequestParams: EmeraldRequestParams = {
    config: axiosConfig,
    addPOSMetaData: true,
    showLoadingComponent: true,
    showLoggingInfo: true,
  };
  return emeraldRequest<CartCustomerData>(emeraldRequestParams);
};

export const voidReturn = async (lineId: number): Promise<CartResponse> => {
  const transactionId = store.getState().transaction.transactionId;
  const axiosConfig: AxiosRequestConfig = {
    url: `/returns/${transactionId}/void/${lineId}`,
    method: 'DELETE',
  };

  const emeraldRequestParams: EmeraldRequestParams = {
    config: axiosConfig,
    addPOSMetaData: true,
    showLoadingComponent: true,
    showLoggingInfo: true,
  };

  return emeraldRequest<CartResponse>(emeraldRequestParams);
};

export const getStoredValueReward = async (): Promise<IPagedData<GetRewardApprovals>> => {
  const transactionId = store.getState().transaction.transactionId;
  const axiosConfig: AxiosRequestConfig = {
    url: `/transaction-management/transactions/${transactionId}/reward-approvals`,
    method: 'GET',
  };

  const emeraldRequestParams: EmeraldRequestParams = {
    config: axiosConfig,
    addPOSMetaData: true,
    showLoadingComponent: true,
    showLoggingInfo: true,
  };

  return emeraldRequest<IPagedData<GetRewardApprovals>>(emeraldRequestParams);
};

export const redeemStoredValueReward = async (
  reward: RewardApprovalBody,
): Promise<CartResponse> => {
  const transactionId = store.getState().transaction.transactionId;
  const msvReward: GetRewardApprovals = {
    redeemLoyalty: {
      approved: reward.approved,
      programId: reward.programId,
      promotionId: reward.promotionId,
      amount: 0,
    },
  };

  if (reward.approved) {
    msvReward.redeemLoyalty.amount = reward.amount;
  }

  const axiosConfig: AxiosRequestConfig = {
    url: `/transaction-management/transactions/${transactionId}/reward-approvals/add`,
    method: 'POST',
    data: msvReward,
  };

  const emeraldRequestParams: EmeraldRequestParams = {
    config: axiosConfig,
    addPOSMetaData: true,
    showLoggingInfo: true,
  };

  return emeraldRequest<CartResponse>(emeraldRequestParams);
};
